// Command lifecycle for server call actions (UX spec 7.4). Each user intent gets one
// Idempotency-Key and the call's expected_version. Duplicate activations while a
// command is pending are ignored. Uncertain outcomes retain the SAME key. Only a
// definitive version-conflict rejection permits a refreshed termination request.
import type { Call, CallAction, CommandReceipt, CreateCall } from '@bcio-voice/contracts';
import { ApiRequestError, type ApiClient, type DataEnvelope, isApiRequestError } from '../lib/api.ts';
import { commandPollDelay, newIdempotencyKey } from '../lib/backoff.ts';

export type ActionName = CallAction['action'];
export type CommandPhase = 'pending' | 'applied' | 'failed' | 'unknown';
export const OUTBOUND_SLOT = '__outbound__';

export interface CommandState {
  slot: string;
  callId: string | null;
  action: ActionName | 'start';
  key: string;
  commandId: string | null;
  phase: CommandPhase;
  serverStatus: CommandReceipt['status'] | null;
  error: ApiRequestError | null;
  startedAt: number;
  polls: number;
}

interface PendingRequest {
  path: string;
  body: unknown;
}

export interface CommandControllerOptions {
  api: Pick<ApiClient, 'request'>;
  onCall: (call: Call) => void;
  refetchCall: (callId: string) => Promise<void>;
  onChange: () => void;
  now?: () => number;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
  newKey?: () => string;
  maxPolls?: number;
}

export class CommandController {
  private readonly states = new Map<string, CommandState>();
  private readonly requests = new Map<string, PendingRequest>();
  private readonly timers = new Map<string, unknown>();
  private disposed = false;
  private readonly now: () => number;
  private readonly setTimer: (fn: () => void, ms: number) => unknown;
  private readonly clearTimer: (handle: unknown) => void;
  private readonly newKey: () => string;

  constructor(private readonly options: CommandControllerOptions) {
    this.now = options.now ?? Date.now;
    this.setTimer = options.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
    this.clearTimer = options.clearTimer ?? (h => clearTimeout(h as ReturnType<typeof setTimeout>));
    this.newKey = options.newKey ?? (() => newIdempotencyKey('web'));
  }

  get(slot: string): CommandState | null {
    return this.states.get(slot) ?? null;
  }

  isBusy(slot: string): boolean {
    const s = this.states.get(slot);
    return !!s && (s.phase === 'pending' || s.phase === 'unknown');
  }

  /** Sends a call action. Returns null when ignored (duplicate, not allowed, disposed). */
  async act(call: Call, action: ActionName, digits?: string): Promise<CommandState | null> {
    if (this.disposed) return null;
    // Ending a call always gets through; any other action waits for the pending one.
    const ending = action === 'hangup' || action === 'reject';
    const current = this.states.get(call.id);
    const staleEnd = ending && !!current && (current.phase === 'unknown' || this.now() - current.startedAt >= 4000);
    if (this.isBusy(call.id) && !staleEnd && (!ending || current?.action === 'hangup' || current?.action === 'reject')) return null;
    if (ending ? call.state === 'ended' || call.state === 'failed' : !call.allowed_actions.includes(action)) return null;
    if (action === 'dtmf' && !digits) return null;
    const body: CallAction = action === 'dtmf' ? { action, expected_version: call.version, digits: digits ?? '' } : { action, expected_version: call.version };
    return this.send(call.id, call.id, action, { path: `/calls/${encodeURIComponent(call.id)}/actions`, body });
  }

  /** Starts an outbound call through POST /calls (server-originated; never a client dialstring). */
  async start(request: CreateCall): Promise<CommandState | null> {
    if (this.disposed || this.isBusy(OUTBOUND_SLOT)) return null;
    return this.send(OUTBOUND_SLOT, null, 'start', { path: '/calls', body: request });
  }

  /** Reconcile an uncertain command: poll by ID, or re-send the identical request with the same key. */
  async checkAgain(slot: string): Promise<void> {
    const state = this.states.get(slot);
    if (!state || state.phase !== 'unknown') return;
    if (state.commandId) {
      this.clearPoll(slot);
      await this.poll(slot);
      return;
    }
    const request = this.requests.get(slot);
    if (!request) return;
    this.patch(slot, { phase: 'pending', error: null });
    await this.post(slot, request);
  }

  dismiss(slot: string): void {
    // An in-flight request stays tracked; only settled or uncertain commands can be dismissed.
    if (this.states.get(slot)?.phase === 'pending') return;
    this.clearPoll(slot);
    this.states.delete(slot);
    this.requests.delete(slot);
    this.options.onChange();
  }

  dispose(): void {
    this.disposed = true;
    for (const slot of [...this.timers.keys()]) this.clearPoll(slot);
  }

  private async send(slot: string, callId: string | null, action: CommandState['action'], request: PendingRequest): Promise<CommandState | null> {
    this.clearPoll(slot);
    this.states.set(slot, { slot, callId, action, key: this.newKey(), commandId: null, phase: 'pending', serverStatus: null, error: null, startedAt: this.now(), polls: 0 });
    this.requests.set(slot, request);
    this.options.onChange();
    await this.post(slot, request);
    return this.states.get(slot) ?? null;
  }

  private async post(slot: string, request: PendingRequest, refreshVersion = true): Promise<void> {
    const state = this.states.get(slot);
    if (!state) return;
    try {
      const envelope = await this.options.api.request<DataEnvelope<CommandReceipt>>(request.path, {
        method: 'POST',
        body: JSON.stringify(request.body),
        idempotencyKey: state.key,
      });
      if (this.states.get(slot)?.key === state.key) this.applyReceipt(slot, envelope.data);
    } catch (error) {
      if (this.disposed || this.states.get(slot)?.key !== state.key) return;
      const apiError = isApiRequestError(error)
        ? error
        : new ApiRequestError({ status: 0, code: 'client_error', message: 'The action could not be sent.', outcomeUnknown: true });
      if (refreshVersion && !apiError.outcomeUnknown && apiError.status === 409 && apiError.code === 'version_conflict' && state.callId && (state.action === 'hangup' || state.action === 'reject')) {
        try {
          const latest = await this.options.api.request<DataEnvelope<Call>>(`/calls/${encodeURIComponent(state.callId)}`);
          if (this.disposed || this.states.get(slot)?.key !== state.key) return;
          this.options.onCall(latest.data);
          if (latest.data.state === 'ended' || latest.data.state === 'failed') {
            this.patch(slot, { phase: 'applied', serverStatus: 'applied', error: null });
            return;
          }
          const refreshed = { ...request, body: { ...(request.body as CallAction), expected_version: latest.data.version } };
          // A 409 is a definite rejection: no command was accepted. Changing the
          // payload uses a new key; lost/uncertain requests never take this path.
          this.patch(slot, { key: this.newKey() });
          this.requests.set(slot, refreshed);
          await this.post(slot, refreshed, false);
          return;
        } catch {
          if (this.disposed || this.states.get(slot)?.key !== state.key) return;
        }
      }
      if (apiError.outcomeUnknown) {
        this.patch(slot, { phase: 'unknown', error: apiError });
        if (state.callId) void this.options.refetchCall(state.callId).catch(() => undefined);
        return;
      }
      this.patch(slot, { phase: 'failed', error: apiError });
      if (state.callId && (apiError.code === 'version_conflict' || apiError.code === 'state_conflict' || apiError.status === 409)) {
        void this.options.refetchCall(state.callId).catch(() => undefined);
      }
    }
  }

  private applyReceipt(slot: string, receipt: CommandReceipt): void {
    if (this.disposed) return;
    this.options.onCall(receipt.call);
    const phase: CommandPhase = receipt.status === 'applied' ? 'applied' : receipt.status === 'failed' ? 'failed' : receipt.status === 'unknown' ? 'unknown' : 'pending';
    this.patch(slot, { callId: receipt.call.id, commandId: receipt.command_id, serverStatus: receipt.status, phase, error: null });
    if (phase === 'pending' || phase === 'unknown') this.schedulePoll(slot);
    else this.clearPoll(slot);
  }

  private schedulePoll(slot: string): void {
    const state = this.states.get(slot);
    if (!state || this.disposed) return;
    const max = this.options.maxPolls ?? 20;
    if (state.polls >= max) {
      this.patch(slot, { phase: 'unknown' });
      return;
    }
    this.clearPoll(slot);
    this.timers.set(slot, this.setTimer(() => {
      this.timers.delete(slot);
      void this.poll(slot);
    }, commandPollDelay(state.polls)));
  }

  private async poll(slot: string): Promise<void> {
    const state = this.states.get(slot);
    if (!state?.commandId || this.disposed) return;
    this.patch(slot, { polls: state.polls + 1 });
    try {
      const envelope = await this.options.api.request<DataEnvelope<CommandReceipt>>(`/commands/${encodeURIComponent(state.commandId)}`);
      if (this.states.get(slot)?.key === state.key) this.applyReceipt(slot, envelope.data);
    } catch (error) {
      if (this.disposed || this.states.get(slot)?.key !== state.key) return;
      this.patch(slot, { phase: 'unknown', error: isApiRequestError(error) ? error : null });
      this.schedulePoll(slot);
    }
  }

  private clearPoll(slot: string): void {
    const handle = this.timers.get(slot);
    if (handle !== undefined) this.clearTimer(handle);
    this.timers.delete(slot);
  }

  private patch(slot: string, patch: Partial<CommandState>): void {
    const state = this.states.get(slot);
    if (!state) return;
    this.states.set(slot, { ...state, ...patch });
    this.options.onChange();
  }
}

/** UX copy for a control's pending label (the control keeps its own label). */
export function commandStatusText(state: CommandState | null): string | null {
  if (!state) return null;
  const verb: Record<string, string> = { start: 'Starting call', answer: 'Answering', reject: 'Declining', hangup: 'Ending call', hold: 'Holding', resume: 'Resuming', dtmf: 'Sending keys' };
  switch (state.phase) {
    case 'pending':
      return `${verb[state.action] ?? 'Working'}…`;
    case 'unknown':
      return 'Not confirmed yet. Checking with the phone system…';
    case 'failed':
      return state.error?.message ?? 'The phone system rejected the action.';
    default:
      return null;
  }
}
